// Policy term length + premium math shared across every place a premium
// is entered for an insurance policy: the manual add form, the Carrier
// Data editor, and the renewal confirm/apply flow.
//
// WHY THIS EXISTS (Grant, 2026-08-11). Policies don't all run on a
// calendar year — 6-month auto terms are ~9% of the active book, plus a
// handful of 3-month and other lengths. `annual_premium` is the number
// every insurance total sums (Brandon's dashboard, household premium
// totals); `term_premium` is what the carrier actually quotes for
// whatever term the policy runs. The two are equal only when the term
// is 12 months. Every doorway that asks a human for "the premium" has
// to say which one it wants and compute the other — otherwise a
// 6-month figure gets typed into the annual box (it did, twice, on
// 2026-08-11) and every downstream total runs wrong.
//
// Carrier-imported policies always carry an accurate term_length_months
// — every spreadsheet importer sets it. Hand-added policies never have:
// neither the add form nor the edit form had a term field before this
// module existed, so all 87 manually-added active policies in
// production have term_length_months = NULL. That's the gap this closes.

/** The term lengths that actually occur in the book, for a picker UI.
 *  "Other" (a free-typed number) covers the rest. */
export const COMMON_TERM_LENGTHS_MONTHS = [12, 6, 3] as const;

/** Generous ceiling — a term longer than this is a data-entry error
 *  (a year mistyped as a month count, etc.), not a real policy term. */
export const TERM_LENGTH_MONTHS_MAX = 60;

/** Human label for a term length, used in field labels and messages
 *  ("Premium (6-month term)"). Null and 12 both read as "annual" — an
 *  unset term is assumed calendar-year, matching
 *  DEFAULT_TERM_LENGTH_MONTHS in renewal-term-rollforward.ts. */
export function describeTermLength(termLengthMonths: number | null): string {
  if (termLengthMonths === null || termLengthMonths === 12) return "annual";
  return `${termLengthMonths}-month term`;
}

/**
 * annual = term × 12 ÷ termLengthMonths. A null/zero/12-month term is
 * the annual case: the two figures are identical.
 */
export function annualizePremiumForTerm(
  termPremium: number,
  termLengthMonths: number | null,
): number {
  if (termLengthMonths === null || termLengthMonths <= 0 || termLengthMonths === 12) {
    return termPremium;
  }
  return Math.round(((termPremium * 12) / termLengthMonths) * 100) / 100;
}

/** The inverse of annualizePremiumForTerm — derives what THIS term's
 *  premium must be from the annual figure. */
export function termPremiumFromAnnual(
  annualPremium: number,
  termLengthMonths: number | null,
): number {
  if (termLengthMonths === null || termLengthMonths <= 0 || termLengthMonths === 12) {
    return annualPremium;
  }
  return Math.round(((annualPremium * termLengthMonths) / 12) * 100) / 100;
}

export interface PremiumPair {
  termPremium: number | null;
  annualPremium: number | null;
}

/**
 * Fills in whichever of term/annual premium the caller didn't supply,
 * from the policy's term length. Both supplied → returned verbatim (an
 * explicit override is trusted, never re-derived — a mid-term
 * endorsement can legitimately break the simple ratio). Neither
 * supplied → both null.
 */
export function deriveMissingPremium(
  input: { termPremium?: number | null; annualPremium?: number | null },
  termLengthMonths: number | null,
): PremiumPair {
  const term = input.termPremium ?? null;
  const annual = input.annualPremium ?? null;
  if (term !== null && annual !== null) return { termPremium: term, annualPremium: annual };
  if (term !== null) {
    return { termPremium: term, annualPremium: annualizePremiumForTerm(term, termLengthMonths) };
  }
  if (annual !== null) {
    return { termPremium: termPremiumFromAnnual(annual, termLengthMonths), annualPremium: annual };
  }
  return { termPremium: null, annualPremium: null };
}

/** A YYYY-MM-DD date, or a full ISO timestamp — accepts either, uses
 *  only the date part. Returns null for anything unparseable. */
function parseIsoDateParts(
  value: string,
): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return { year, month, day };
}

/**
 * Estimates a policy's term length FROM ITS OWN DATES when
 * term_length_months isn't recorded — a policy running Feb 10 → Aug 10
 * is a 6-month term whether or not anyone wrote that down. Rounds to
 * the nearest whole month; returns null when the dates are missing,
 * unparseable, or the gap is nonsensical (zero/negative, or beyond
 * TERM_LENGTH_MONTHS_MAX).
 */
export function inferTermLengthFromDates(
  effectiveDate: string | null,
  expirationDate: string | null,
): number | null {
  if (!effectiveDate || !expirationDate) return null;
  const eff = parseIsoDateParts(effectiveDate);
  const exp = parseIsoDateParts(expirationDate);
  if (!eff || !exp) return null;
  const wholeMonths = (exp.year - eff.year) * 12 + (exp.month - eff.month);
  // Day-of-month drift (month-length wobble, e.g. Feb 28 → Aug 28 is
  // exactly 6 months even though the day count differs from Aug 31)
  // nudges the estimate by a fraction of a month before rounding.
  const dayFraction = (exp.day - eff.day) / 30;
  const rounded = Math.round(wholeMonths + dayFraction);
  if (rounded <= 0 || rounded > TERM_LENGTH_MONTHS_MAX) return null;
  return rounded;
}

/** Validates a user-supplied term_length_months: a positive whole
 *  number of months, or null to clear/leave unknown. */
export function normalizeTermLengthInput(
  raw: unknown,
): { ok: true; value: number | null } | { ok: false } {
  if (raw === null || raw === undefined) return { ok: true, value: null };
  if (
    typeof raw !== "number" ||
    !Number.isInteger(raw) ||
    raw <= 0 ||
    raw > TERM_LENGTH_MONTHS_MAX
  ) {
    return { ok: false };
  }
  return { ok: true, value: raw };
}
